import type { CSSProperties } from 'react'
import type { EmailRule } from '@/types/emailRule'
import type { FieldDefinition, FieldOption } from '@/tableControls'
import { readStoredColumnWidths, saveColumnWidths } from '@/tableControls'

export const EMAIL_RULES_PAGE_KEY = 'email-rules'

export const LEGACY_WIDTH_KEYS = [
  'bookkeeper:email-rules-table-widths',
  'invoice-minion:email-rules-table-widths',
] as const

export const ALL_COLUMN_KEYS = [
  'vendor_name',
  'from_filter',
  'subject_contains',
  'email_has_words',
  'destinations',
  'status',
  'pdf_hints',
  'notes',
  'actions',
] as const

export type EmailRulesColumnKey = (typeof ALL_COLUMN_KEYS)[number]

export const EMAIL_RULES_DEFAULT_WIDTHS: Record<string, number> = {
  vendor_name: 168,
  from_filter: 152,
  subject_contains: 152,
  email_has_words: 128,
  destinations: 112,
  status: 104,
  pdf_hints: 88,
  notes: 208,
  actions: 148,
}

export const EMAIL_RULES_COLUMN_LABELS: Record<EmailRulesColumnKey, string> = {
  vendor_name: 'Vendor',
  from_filter: 'From',
  subject_contains: 'Subject',
  email_has_words: 'Has Words',
  destinations: 'Dest',
  status: 'Status',
  pdf_hints: 'Parsing Hints',
  notes: 'Notes',
  actions: 'Actions',
}

const LOCKED_COLUMN_KEYS = new Set<EmailRulesColumnKey>(['vendor_name', 'actions'])

const SORTABLE_COLUMN_KEYS = new Set<EmailRulesColumnKey>([
  'vendor_name',
  'from_filter',
  'subject_contains',
  'email_has_words',
  'destinations',
  'status',
  'pdf_hints',
  'notes',
])

export const EMAIL_RULES_DEFAULT_SORT = [{ field: 'vendor_name', direction: 'asc' as const }]

export const EMAIL_RULES_STATUS_OPTIONS: FieldOption[] = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'draft', label: 'Draft' },
]

export const EMAIL_RULES_FIELD_DEFINITIONS: FieldDefinition[] = ALL_COLUMN_KEYS.map((key) => {
  if (key === 'pdf_hints') {
    return {
      key,
      label: EMAIL_RULES_COLUMN_LABELS[key],
      type: 'text',
      locked: LOCKED_COLUMN_KEYS.has(key),
      defaultVisible: true,
      sortable: SORTABLE_COLUMN_KEYS.has(key),
      accessor: (row: unknown) =>
        (row as EmailRule).extraction_directive?.trim() ? '1' : '0',
    }
  }
  if (key === 'destinations') {
    return {
      key,
      label: EMAIL_RULES_COLUMN_LABELS[key],
      type: 'text',
      locked: LOCKED_COLUMN_KEYS.has(key),
      defaultVisible: true,
      sortable: SORTABLE_COLUMN_KEYS.has(key),
      accessor: (row: unknown) => (row as EmailRule).destinations.join(',').toLowerCase(),
    }
  }
  if (key === 'actions') {
    return {
      key,
      label: EMAIL_RULES_COLUMN_LABELS[key],
      type: 'text',
      locked: LOCKED_COLUMN_KEYS.has(key),
      defaultVisible: true,
      sortable: false,
    }
  }
  const rowKey = key as keyof EmailRule
  return {
    key,
    label: EMAIL_RULES_COLUMN_LABELS[key],
    type: 'text',
    locked: LOCKED_COLUMN_KEYS.has(key),
    defaultVisible: true,
    sortable: SORTABLE_COLUMN_KEYS.has(key),
    accessor: (row: unknown) => {
      const value = (row as EmailRule)[rowKey]
      if (typeof value === 'string') return value.toLowerCase()
      return value ?? ''
    },
  }
})

export function fixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

export function migrateLegacyColumnWidths(): void {
  const existing = readStoredColumnWidths(EMAIL_RULES_PAGE_KEY)
  if (Object.keys(existing).length > 0) return
  for (const legacyKey of LEGACY_WIDTH_KEYS) {
    try {
      const raw = localStorage.getItem(legacyKey)
      if (!raw) continue
      const parsed = JSON.parse(raw) as Record<string, unknown>
      const widths: Record<string, number> = {}
      for (const key of ALL_COLUMN_KEYS) {
        const value = parsed[key]
        if (typeof value === 'number' && Number.isFinite(value)) {
          widths[key] = value
        }
      }
      if (Object.keys(widths).length > 0) {
        saveColumnWidths(EMAIL_RULES_PAGE_KEY, widths)
      }
      return
    } catch {
      /* try next legacy key */
    }
  }
}

export function isEmailRulesColumnSortable(key: string): boolean {
  return SORTABLE_COLUMN_KEYS.has(key as EmailRulesColumnKey)
}

/** FilterBuilderDrawer fields (table filters only — not column picker / sort). */
export function buildEmailRulesFilterFields(
  destinationOptions: FieldOption[]
): FieldDefinition[] {
  return [
    {
      key: 'vendor_name',
      label: 'Vendor',
      type: 'text',
      filterable: true,
      accessor: (row) => (row as EmailRule).vendor_name ?? '',
    },
    {
      key: 'from_filter',
      label: 'From Filter',
      type: 'text',
      filterable: true,
      accessor: (row) => (row as EmailRule).from_filter ?? '',
    },
    {
      key: 'subject_contains',
      label: 'Subject Contains',
      type: 'text',
      filterable: true,
      accessor: (row) => (row as EmailRule).subject_contains ?? '',
    },
    {
      key: 'destination',
      label: 'Destination',
      type: 'multiSelect',
      filterable: true,
      options: destinationOptions,
      accessor: (row) => (row as EmailRule).destinations,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      filterable: true,
      options: EMAIL_RULES_STATUS_OPTIONS,
      accessor: (row) => (row as EmailRule).status,
    },
  ]
}
